import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { getAllDataWithFlag } from '../services/CommonService';
import EventCard from '../components/User/EventNews/EventCard';
import { imgURL } from '../components/Config';
import { getEventSchedule, getEventStatus } from '../Utils/eventSchedule';
import './EventPage.css';

const EVENT_TABS = [
  { id: 'all', label: 'All Events', icon: 'bi-grid' },
  { id: 'upcoming', label: 'Up coming', icon: 'bi-calendar-plus' },
  { id: 'ongoing', label: 'On Going', icon: 'bi-broadcast' },
  { id: 'past', label: 'Past', icon: 'bi-clock-history' },
];

const readStoredList = (key) => {
  try {
    const value = JSON.parse(localStorage.getItem(key) || '[]');
    return Array.isArray(value) ? value : [];
  } catch {
    return [];
  }
};

const EventPage = ({ projectId }) => {
  const [events, setEvents] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  const [now, setNow] = useState(Date.now);
  const [lookupData] = useState(() => readStoredList('lookup'));
  const tabRefs = useRef([]);
  const instanceId = useId();
  const hasProject = projectId !== undefined && projectId !== null && projectId !== '';

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    setActiveTab('all');

    const fetchEvents = async () => {
      try {
        const response = await getAllDataWithFlag(6);
        const records = response?.data?.event
          || response?.data?.data?.event
          || response?.event
          || [];
        const eventRecords = Array.isArray(records) ? records : [];
        const visibleEvents = eventRecords.filter((event) => event
          && event.is_deleted !== true
          && String(event.is_deleted) !== '1'
          && (!hasProject || String(event.project_id) === String(projectId)));
        if (!cancelled) setEvents(visibleEvents);
      } catch (err) {
        if (!cancelled) {
          setEvents([]);
          setError('We could not load the events. Please try again.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchEvents();
    return () => { cancelled = true; };
  }, [projectId, hasProject, retry]);

  const scheduledEvents = useMemo(() => events.map((event) => ({
    event,
    schedule: getEventSchedule(event),
  })), [events]);

  // Refresh at each start/end boundary, and check for clock changes every minute.
  useEffect(() => {
    const boundaries = scheduledEvents.flatMap(({ schedule }) => [
      schedule.start?.valueOf(), schedule.end?.valueOf(),
    ]).filter((time) => time > now);
    const nextChange = Math.min(now + 60000, ...boundaries);
    const timer = setTimeout(() => setNow(Date.now()), Math.max(1, nextChange - Date.now()));
    return () => clearTimeout(timer);
  }, [scheduledEvents, now]);

  const classifiedEvents = useMemo(() => scheduledEvents.map((item) => ({
    ...item,
    status: getEventStatus(item.schedule, now),
  })), [scheduledEvents, now]);

  const counts = classifiedEvents.reduce((result, { status }) => {
    result.all += 1;
    result[status] = (result[status] || 0) + 1;
    return result;
  }, { all: 0, upcoming: 0, ongoing: 0, past: 0 });

  const visibleEvents = classifiedEvents
    .filter(({ status }) => activeTab === 'all' || status === activeTab)
    .sort((a, b) => {
      if (!a.schedule.start) return b.schedule.start ? 1 : 0;
      if (!b.schedule.start) return -1;
      if (activeTab === 'upcoming') return a.schedule.start.valueOf() - b.schedule.start.valueOf();
      if (activeTab === 'ongoing') return a.schedule.end.valueOf() - b.schedule.end.valueOf();
      if (activeTab === 'past') return b.schedule.end.valueOf() - a.schedule.end.valueOf();
      return b.schedule.start.valueOf() - a.schedule.start.valueOf();
    });

  const handleTabKeyDown = (event, index) => {
    let nextIndex;
    if (event.key === 'ArrowRight') nextIndex = (index + 1) % EVENT_TABS.length;
    else if (event.key === 'ArrowLeft') nextIndex = (index + EVENT_TABS.length - 1) % EVENT_TABS.length;
    else if (event.key === 'Home') nextIndex = 0;
    else if (event.key === 'End') nextIndex = EVENT_TABS.length - 1;
    else return;
    event.preventDefault();
    setActiveTab(EVENT_TABS[nextIndex].id);
    tabRefs.current[nextIndex]?.focus();
  };

  const banner = readStoredList('banners').find((item) => String(item.banner_type) === '7');
  const bannerUrl = banner?.image_path
    ? (/^https?:\/\//i.test(banner.image_path) ? banner.image_path : String(imgURL || '') + banner.image_path)
    : '/assets/images/events.png';
  const selectedTab = EVENT_TABS.find((tab) => tab.id === activeTab);
  const emptyMessage = {
    all: hasProject ? 'There are no events for this project yet.' : 'New events will be announced here. Please check back soon.',
    upcoming: 'There are no upcoming events at the moment. Explore our past events while we plan what is next.',
    ongoing: 'There are no events taking place right now. Explore upcoming events or revisit past sessions.',
    past: 'Past events will appear here once they have ended.',
  };

  return (
    <section className={'events-page' + (hasProject ? ' events-page--embedded' : '')}>
      {!hasProject && (
        <header className="events-hero">
          <img className="events-hero__image" src={bannerUrl} alt="" onError={(event) => { event.currentTarget.style.display = 'none'; }} />
          <div className="events-shell events-hero__content">
            <h1>Project events</h1>
            <p>Discover upcoming sessions, join ongoing conversations, and revisit past events.</p>
          </div>
        </header>
      )}

      <div className="events-shell events-content">
        <div className="events-tabs" role="tablist" aria-label="Events by status">
          {EVENT_TABS.map((tab, index) => (
            <button
              key={tab.id}
              ref={(element) => { tabRefs.current[index] = element; }}
              type="button"
              role="tab"
              id={instanceId + '-tab-' + tab.id}
              aria-selected={activeTab === tab.id}
              aria-controls={instanceId + '-panel'}
              tabIndex={activeTab === tab.id ? 0 : -1}
              className={'events-tab' + (activeTab === tab.id ? ' events-tab--active' : '')}
              onClick={() => setActiveTab(tab.id)}
              onKeyDown={(event) => handleTabKeyDown(event, index)}
            >
              <i className={'bi ' + tab.icon} aria-hidden="true"></i>
              <span>{tab.label}</span>
              <span className="events-tab__count">{loading ? '?' : counts[tab.id]}</span>
            </button>
          ))}
        </div>

        <div id={instanceId + '-panel'} role="tabpanel" aria-labelledby={instanceId + '-tab-' + activeTab} tabIndex={0} aria-busy={loading} className="events-panel">
          {loading ? (
            <div className="events-state" role="status"><span className="spinner-border" aria-hidden="true"></span><p>Loading events...</p></div>
          ) : error ? (
            <div className="events-state" role="alert">
              <i className="bi bi-cloud-slash events-state__icon" aria-hidden="true"></i>
              <h3>Events are unavailable</h3><p>{error}</p>
              <button type="button" className="events-reset" onClick={() => setRetry((value) => value + 1)}>Try again</button>
            </div>
          ) : (
            <>
              <p className="events-result-count" role="status">{visibleEvents.length} {visibleEvents.length === 1 ? 'event' : 'events'}{activeTab !== 'all' && ' ? ' + selectedTab.label}</p>
              {visibleEvents.length ? (
                <div className="events-grid">
                  {visibleEvents.map(({ event, schedule, status }) => (
                    <EventCard key={event.id} event={event} lookupData={lookupData} schedule={schedule} status={status} />
                  ))}
                </div>
              ) : (
                <div className="events-state">
                  <i className={'bi ' + selectedTab.icon + ' events-state__icon'} aria-hidden="true"></i>
                  <h3>{activeTab === 'all' ? 'No events yet' : 'No ' + ({ upcoming: 'upcoming', ongoing: 'ongoing', past: 'past' })[activeTab] + ' events'}</h3>
                  <p>{emptyMessage[activeTab]}</p>
                  {activeTab !== 'all' && <button type="button" className="events-reset" onClick={() => setActiveTab('all')}>View all events <i className="bi bi-arrow-right" aria-hidden="true"></i></button>}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default EventPage;
